'use client';

import { useSyncExternalStore } from 'react';
import { useTheme } from '@/providers/theme-provider';
import { useTerminalMode } from '@/hooks/use-terminal-mode';
import { themes, ThemeName, getThemeConfig, ThemeConfig } from '@/lib/theme-config';

export interface UseThemeConfigReturn {
  /** The effective theme name after resolving terminal mode and system preference */
  theme: ThemeName;
  /** The complete theme configuration object */
  config: ThemeConfig;
  /** Whether terminal mode is active */
  isTerminal: boolean;
  /** Whether light theme is active */
  isLight: boolean;
  /** Whether dark theme is active */
  isDark: boolean;
  /** Whether the theme has glow effects enabled */
  hasGlow: boolean;
  /** Whether the theme has scanlines enabled */
  hasScanlines: boolean;
  /** Whether the theme has CRT overlay enabled */
  hasCRT: boolean;
  /** Whether the theme uses monospace fonts for data display */
  useMono: boolean;
  /** Whether the component has mounted (hydration-safe) */
  mounted: boolean;
}

// Mount-state external store: no-op subscription, server reports unmounted,
// client reports mounted. Lets useSyncExternalStore hydrate without a flash.
const subscribeMounted = () => () => {};
const getMountedClientSnapshot = () => true;
const getMountedServerSnapshot = () => false;

/** False in the server render and during hydration, true after. */
export function useMounted(): boolean {
  return useSyncExternalStore(subscribeMounted, getMountedClientSnapshot, getMountedServerSnapshot);
}

/**
 * Hook that combines theme state with configuration.
 *
 * HYDRATION SAFETY: This hook includes a `mounted` boolean that is false during SSR
 * and becomes true after the first client render. Use this to conditionally apply
 * terminal-specific styling only after hydration to prevent mismatches.
 *
 * This hook determines the effective theme by:
 * 1. Checking if terminal mode is active (overrides everything)
 * 2. Resolving 'system' theme to 'dark' (default fallback)
 * 3. Otherwise using the current theme setting
 *
 * @returns Theme configuration and helper booleans for common checks
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isTerminal, hasGlow, config, mounted } = useThemeConfig();
 *
 *   // Use mounted to prevent hydration mismatch
 *   const showTerminalStyles = mounted && isTerminal;
 *
 *   return (
 *     <div className={showTerminalStyles ? 'terminal-glow' : ''}>
 *       {showTerminalStyles && <Scanlines />}
 *       <span style={{ fontFamily: config.fonts.data === 'mono' ? 'monospace' : 'sans-serif' }}>
 *         Data
 *       </span>
 *     </div>
 *   );
 * }
 * ```
 */
export function useThemeConfig(): UseThemeConfigReturn {
  const { theme } = useTheme();
  const { isTerminalMode } = useTerminalMode();

  // Hydration-safe: track mount state via useSyncExternalStore so the
  // server snapshot (false) and client snapshot (true) hydrate in a single
  // commit with no post-mount re-render flash.
  const mounted = useSyncExternalStore(
    subscribeMounted,
    getMountedClientSnapshot,
    getMountedServerSnapshot
  );

  // Determine effective theme:
  // 1. Terminal mode takes precedence
  // 2. 'system' defaults to 'dark'
  // 3. Otherwise use the current theme
  const effectiveTheme: ThemeName = isTerminalMode
    ? 'terminal'
    : (theme === 'system' ? 'dark' : theme as ThemeName);

  const config = getThemeConfig(effectiveTheme);

  return {
    theme: effectiveTheme,
    config,
    // Convenience booleans
    isTerminal: effectiveTheme === 'terminal',
    isLight: effectiveTheme === 'light',
    isDark: effectiveTheme === 'dark',
    // Effect checks
    hasGlow: config.effects.glow,
    hasScanlines: config.effects.scanlines,
    hasCRT: config.effects.crtOverlay,
    useMono: config.fonts.data === 'mono',
    // Hydration safety
    mounted,
  };
}
